import { DownloadSVG } from 'common/assets';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import useNavigation from 'hooks/use-navigation';
import invariant from 'invariant';
import useExportReport from 'legacy-modules/report/hooks/use-export-report';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';

interface ContextProps {
  tableId: string;
  setTableId: () => void;
}

const Context = React.createContext<ContextProps>({
  tableId: uuidv4(),
  setTableId: () => {},
});

interface PivotTableProviderProps {
  children: (ExportButton: () => React.ReactElement) => React.ReactNode;
  filter: any;
  pivot: any;
}

export function PivotTableProvider(props: PivotTableProviderProps) {
  const [tableId, setState] = React.useState<string>(uuidv4());
  const { t } = useTranslation();
  const { activeKey } = useNavigation();
  const setTableId = React.useCallback(() => {
    setState(uuidv4());
  }, []);
  const ExportButtons = document.getElementById(tableId);
  const { onExport, isLoading, type, permission, isHideExport } =
    useExportReport(activeKey);

  const onClickExport = React.useCallback(async () => {
    if (!!type && !!permission) {
      try {
        await onExport(props.filter, props.pivot);
      } catch (error) {
        notification.error({ message: error.message });
      }
    } else {
      if (typeof window !== 'undefined') {
        const { TableExport } = require('tableexport');
        const ExportInstance = new TableExport(ExportButtons as any, {
          formats: ['xlsx'],
          exportButtons: false,
          filename: activeKey || 'report',
        });
        const XLSX = (ExportInstance as any).CONSTANTS.FORMAT.XLSX;
        const ExportData = ExportInstance.getExportData()[tableId][XLSX];
        ExportInstance.export2file(
          ExportData.data,
          ExportData.mimeType,
          ExportData.filename,
          ExportData.fileExtension,
        );
      }
    }
  }, [
    ExportButtons,
    activeKey,
    onExport,
    permission,
    props.filter,
    props.pivot,
    tableId,
    type,
  ]);

  return (
    <Context.Provider value={{ tableId, setTableId }}>
      {props.children(() => {
        if (!Object.keys(props.filter).length && isHideExport) return <></>;
        return (
          <Button
            buttonType="tertiary"
            onClick={onClickExport}
            startEnhancer={(color, size) => (
              <DownloadSVG color={color} size={size} />
            )}
            style={{ minWidth: 180 }}
            isLoading={isLoading}
          >
            {t('common:export_to_excel')}
          </Button>
        );
      })}
    </Context.Provider>
  );
}

export function usePivotTable() {
  const context = React.useContext(Context);
  invariant(
    context !== undefined,
    'usePivotTable must be used inside Pivot Table Container',
  );
  return context;
}
